"use client";

import {
  ParcelasTable,
  usePrenotaStore,
  useParcelas,
} from "@PN/inclusao";
import { Banknote, Loader2 } from "lucide-react";
import { useCatalogs } from "store";
import { useMemo, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Combobox,
  Separator,
  cn,
  DialogFooter,
  DialogClose,
} from "visuais";
import { TutorialGuide, useTutorial } from "@PN/_nucleo/TutorialGuide";
import { useAprovarFaturaStore } from "@FN";

export function CondicaoDialogFaturas({ className, fatura }: { className?: string, fatura: any }) {
  const aprovarFaturaStore = useAprovarFaturaStore(); 
  const resetPrenota = usePrenotaStore((state) => state.actions.reset);
  const setTotalNota = usePrenotaStore((state) => state.actions.aux.setTotalNota);
  
  const pagamentos = usePrenotaStore((state) => state.draft.pagamentos);
  const draft = usePrenotaStore((state) => state.draft);
  const setPagamentos = usePrenotaStore(
    (state) => state.actions.draft.setPagamentos
  );

  // Hook para parcelas (inclui busca automática)
  const {
    parcelas,
    isLoading,
    canFetch,
    handleForceRefetch,
    valorNF,
  } = useParcelas();

  const { condicoes } = useCatalogs();
  const condicaoDescricao = useMemo(() => {
    const item = condicoes.getBy("E4_CODIGO", pagamentos.condicaoFinanceira);
    return item
      ? `${item.E4_CODIGO} - ${item.E4_DESCRI.trim()}`
      : "Não selecionada";
  }, [pagamentos.condicaoFinanceira, condicoes]);

  const { markTutorialAsCompleted, isTutorialCompleted } = useTutorial();

  // Estado para controlar se o dialog está aberto
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Validação quando o dialog abre
  useEffect(() => {
    if (isDialogOpen) {
      console.log("💰 [CondicaoDialog] Dialog aberto, validando campos:", {
        temParcelas: parcelas.length > 0,
        parcelasCount: parcelas.length,
        condicaoFinanceira: pagamentos.condicaoFinanceira,
        dataEmissao: draft.documento.dataEmissao,
        valorNF,
        fornecedor: draft.documento.fornecedor,
        loja: draft.documento.loja,
        canFetch,
      });

      // Verifica se tem parcelas salvas
      const temParcelasSalvas = parcelas.length > 0;

      // Verifica se todos os campos obrigatórios estão preenchidos
      const camposObrigatorios = {
        condicaoFinanceira: !!pagamentos.condicaoFinanceira,
        dataEmissao: !!draft.documento.dataEmissao,
        valorNF: fatura.Z15_VALOR > 0,
        fornecedor: !!draft.documento.fornecedor,
        loja: !!draft.documento.loja,
      };

      const todosCamposPreenchidos =
        Object.values(camposObrigatorios).every(Boolean);

      console.log("💰 [CondicaoDialog] Validação:", {
        temParcelasSalvas,
        todosCamposPreenchidos,
        camposObrigatorios,
      });

      // Se não tem parcelas mas pode buscar, força a busca
      if (!temParcelasSalvas && todosCamposPreenchidos && canFetch) {
        console.log(
          "💰 [CondicaoDialog] Não tem parcelas salvas mas pode buscar - forçando busca"
        );
        handleForceRefetch();
      } else if (!temParcelasSalvas && !todosCamposPreenchidos) {
        console.log(
          "💰 [CondicaoDialog] Campos obrigatórios não preenchidos - não pode buscar"
        );
      } else if (temParcelasSalvas) {
        console.log(
          "💰 [CondicaoDialog] Já tem parcelas salvas - não precisa buscar"
        );
      }
    }
  }, [
    isDialogOpen,
    parcelas.length,
    pagamentos.condicaoFinanceira,
    draft.documento.dataEmissao,
    valorNF,
    draft.documento.fornecedor,
    draft.documento.loja,
    canFetch,
    handleForceRefetch,
    fatura.Z15_VALOR,
  ]);

  useEffect(() => {
    if (isDialogOpen) {
      resetPrenota(); 
    }
    if (isDialogOpen && fatura) {
      const hoje = new Date().toISOString().split("T")[0] as `${number}-${number}-${number}`;

      usePrenotaStore.getState().actions.draft.documento.set({
        fornecedor: fatura.Z15_FORNEC,
        loja: fatura.Z15_LOJA,
        dataEmissao: hoje,
        numero: "",
        serie: "",
        especie: "",
        chaveNFe: "",
        despesa: 0,
        frete: 0,
        desconto: 0
      });
      setTotalNota({
        total: String(fatura.Z15_VALOR ?? 0),
        desconto: "0",
        frete: "0",
        despesa: "0",
        icms: "0",
        ipi: "0",
        pis: "0",
        cofins: "0",
      });
    }
  }, [isDialogOpen, fatura, resetPrenota, setTotalNota]);

  // Passos do tutorial
  const tutorialSteps = [
    {
      id: "condicao-pagamento",
      title: "Condição de Pagamento",
      description:
        "Selecione a condição de pagamento que determina como o valor será parcelado e os prazos de vencimento.",
      tip: "💡 Dica: A condição determina automaticamente as parcelas da nota fiscal",
    },
    {
      id: "parcelas",
      title: "Parcelas",
      description:
        "As parcelas são calculadas automaticamente baseadas na condição selecionada. Você pode ajustar as datas de vencimento.",
      tip: "💡 Dica: Verifique se o total das parcelas corresponde ao valor da nota",
    },
    {
      id: "dados-pix",
      title: "Dados PIX",
      description:
        "Configure as informações PIX para facilitar o pagamento. Inclui chave PIX e CPF/CNPJ do destinatário.",
      tip: "💡 Dica: Os dados PIX são opcionais mas facilitam o processo de pagamento",
    },
  ];

  // Trigger label dinâmico com status da busca automática
  const btnLabel = useMemo(() => {
    if (isLoading && canFetch) {
      return "Buscando parcelas...";
    }

    if (condicaoDescricao && condicaoDescricao !== "Não selecionada") {
      const parcelasCount =
        parcelas.length > 0
          ? ` • ${parcelas.length} parcela${parcelas.length > 1 ? "s" : ""}`
          : "";
      return `${condicaoDescricao}${parcelasCount}`;
    }

    return "Configurar financeiro";
  }, [condicaoDescricao, parcelas.length, isLoading, canFetch]);

  const handleCondicaoChange = (value: string | undefined) => {
    if (value) {
      setPagamentos({
        ...pagamentos,
        condicaoFinanceira: value,
      });
    }
  };

  // Função para lidar com o clique do botão salvar
  const handleSalvarClick = () => {
    if (!pagamentos.condicaoFinanceira || parcelas.length === 0) return;

    // Condição de pagamento escolhida
    aprovarFaturaStore.actions.setCond({ E4_CODIGO: pagamentos.condicaoFinanceira });

    // Monta array esperado pela API
    const vencimentos = parcelas.map((p) => ({
      Parcela: p.item,      // número da parcela
      Vencimento: p.vencimento, // data ISO
      Valor: p.valor,       // valor da parcela
    }));

    aprovarFaturaStore.actions.setVencimentos(vencimentos);

    console.log("💰 Vencimentos enviados para store:", {
      cond: pagamentos.condicaoFinanceira,
      vencimentos: vencimentos,
    });
  };

  // Verifica se pode salvar (tem parcelas válidas)
  const canSave =
    parcelas.length > 0 && parcelas.every((p) => p.valor > 0 && p.vencimento);

  return (
    <div className="col-span-3 space-y-2 flex flex-col">
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "flex justify-between w-full overflow-hidden relative",
              className
            )}
          >
            <span className="truncate">{btnLabel}</span>
            <div className="flex items-center gap-2 ml-2">
              {isLoading && canFetch && (
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              )}
              <Banknote className="h-4 w-4" />
            </div>
          </Button>
        </DialogTrigger>
        <DialogContent className="min-h-[500px] flex flex-col items-center">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <TutorialGuide
                steps={tutorialSteps}
                title="Tutorial: Condição de Pagamento"
                description="Aprenda a configurar corretamente a condição de pagamento e parcelas"
                onComplete={() => markTutorialAsCompleted("condicao-pagamento")}
              />
              <span>Financeiro (Condição, PIX e Pagamento)</span>
            </DialogTitle>
          </DialogHeader>
          <Separator />
          <div className="flex flex-col mx-auto w-full gap-5 justify-start">
            <div className="col-span-1 sm:col-span-2 space-y-4">
              <Combobox
                options={condicoes.options}
                value={pagamentos.condicaoFinanceira || ""}
                onChange={handleCondicaoChange}
                placeholder="Selecione a condição"
                className="text-xs"
              />
            </div>

            <ParcelasTable />

          </div>
          <Separator />
          <DialogFooter className="w-full flex justify-end gap-2">
            <DialogClose asChild>
              <Button
                onClick={handleSalvarClick}
                disabled={!canSave}
                className="min-w-[100px]"
              >
                {canSave ? "Salvar" : "Sem parcelas"}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
